import { useMemo } from 'react';
import { useSelector } from 'react-redux';

/**
 * Common hook for billing and trial status logic
 * Used across multiple components to avoid duplication
 * @returns {Object} Trial and billing status information
 */
export const useBillingTrial = () => {
  const {
    billing: {
      plans: {
        isFetching = true,
        active: activeCharges = [],
        app: appInfo = {},
      } = {},
    } = {},
    settings: {
      platformSettings: { bridgeApplicable = false } = {},
    } = {},
  } = useSelector((state) => state);

  const hasActiveSub = useMemo(
    () => (activeCharges || []).some((c) => c?.status === 'active'),
    [activeCharges],
  );

  const status = appInfo?.status;
  const daysLeftRaw = appInfo?.trialDaysLeft;
  const daysLeft = Number.isFinite(daysLeftRaw) ? daysLeftRaw : undefined;

  const isTrialPhase = useMemo(
    () => ['TRIAL', 'INTEGRATION', 'TRIAL_LAST_CALL'].includes(status),
    [status],
  );

  const isDisabled = useMemo(
    () => ['ACTIVE_SWITCHED_OFF', 'SWITCH_OFF'].includes(status),
    [status],
  );

  const hideByDaysHack = Number.isFinite(daysLeft) && daysLeft > 30;
  const isEnded = Number.isFinite(daysLeft) && daysLeft <= 0;

  const shouldShowTrial = bridgeApplicable
    && !isFetching
    && !hasActiveSub
    && Number.isFinite(daysLeft)
    && !hideByDaysHack
    && (isTrialPhase || isEnded);

  const shouldShowDisabled = bridgeApplicable
    && !isFetching
    && isDisabled;

  return {
    // Billing state
    isFetching,
    hasActiveSub,
    activeCharges,
    appInfo,
    status,
    bridgeApplicable,

    // Trial state
    daysLeft,
    trialDaysLeft: daysLeft,
    isTrialPhase,
    isEnded,

    // Disabled state
    isDisabled,

    // Display flags
    shouldShowTrial,
    shouldShowDisabled,
    isTrial: shouldShowTrial,
  };
};
